// Digital commerce calculators: D01 unit economics, D02 ACOS, D03 ROAS, D05 CAC/LTV.
import { useMemo } from "react";
import { unitEconomics, amazonAcos, breakEvenRoas, cacLtv, inr, pct } from "./engines";
import { Field, SliderField, ResultRow, Note, Waterfall, useNumbers } from "./ui";

export function ToolD01() {
  const [v, set] = useNumbers({
    price: 1500, discountPct: 0, gstRatePct: 18, productCost: 600,
    platformFeePct: 5, paymentFeePct: 2, packaging: 30, shipping: 70,
    returnRatePct: 10, reverseShipping: 80, adCostPerOrder: 150,
  });
  const r = useMemo(() => unitEconomics(v), [v]);
  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="space-y-5">
        <Field label="Selling price (incl. GST)" prefix="₹" value={v.price} onChange={set("price")} testid="d01-price" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="GST rate" suffix="%" value={v.gstRatePct} onChange={set("gstRatePct")} testid="d01-gst" />
          <Field label="Discount" suffix="%" value={v.discountPct} onChange={set("discountPct")} testid="d01-discount" />
          <Field label="Product landed cost" prefix="₹" value={v.productCost} onChange={set("productCost")} testid="d01-cost" />
          <Field label="Marketplace fee" suffix="%" value={v.platformFeePct} onChange={set("platformFeePct")} testid="d01-platform" />
          <Field label="Payment fee" suffix="%" value={v.paymentFeePct} onChange={set("paymentFeePct")} testid="d01-payment" />
          <Field label="Packaging / order" prefix="₹" value={v.packaging} onChange={set("packaging")} testid="d01-pack" />
          <Field label="Forward shipping" prefix="₹" value={v.shipping} onChange={set("shipping")} testid="d01-ship" />
          <Field label="Return rate" suffix="%" value={v.returnRatePct} onChange={set("returnRatePct")} testid="d01-returns" />
          <Field label="Reverse shipping / return" prefix="₹" value={v.reverseShipping} onChange={set("reverseShipping")} testid="d01-reverse" />
          <Field label="Ad cost per order" prefix="₹" value={v.adCostPerOrder} onChange={set("adCostPerOrder")} testid="d01-ads" />
        </div>
        <Note>Returns assume 50% of product cost is recovered as resaleable stock — adjust your expectation accordingly. Fixed overheads are excluded by design.</Note>
      </div>
      <div>
        <div className="border border-zinc-200 bg-white p-6">
          <div className="overline">Per-order contribution</div>
          <div className="mt-4 font-serif-display text-4xl" data-testid="d01-result">{inr(r.perPlacedOrderContribution)}</div>
          <div className="text-sm text-zinc-500">per placed order, after expected returns and ads</div>
          <div className="mt-4">
            <ResultRow label="Successful-order contribution" value={inr(r.successContribution)} />
            <ResultRow label="Break-even ad cost per order" value={inr(r.breakEvenAdCost)} tone="good" />
          </div>
        </div>
        <div className="mt-6 border border-zinc-200 bg-white p-6">
          <div className="overline mb-3">Where the money goes</div>
          <Waterfall items={r.waterfall.map((w) => ({ ...w, display: inr(w.value) }))} />
        </div>
      </div>
    </div>
  );
}

export function ToolD02() {
  const [v, set] = useNumbers({ price: 999, referralPct: 15, closingFee: 20, fulfilmentFee: 65, productCost: 420, packingCost: 15, otherVariable: 0, promoCost: 0, targetContributionPct: 10 });
  const r = useMemo(() => amazonAcos(v), [v]);
  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="space-y-5">
        <Field label="Realized selling price (ex-GST)" prefix="₹" value={v.price} onChange={set("price")} testid="d02-price" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Referral fee" suffix="%" value={v.referralPct} onChange={set("referralPct")} testid="d02-referral" />
          <Field label="Closing fee" prefix="₹" value={v.closingFee} onChange={set("closingFee")} testid="d02-closing" />
          <Field label="Fulfilment fee" prefix="₹" value={v.fulfilmentFee} onChange={set("fulfilmentFee")} testid="d02-fba" />
          <Field label="Product cost" prefix="₹" value={v.productCost} onChange={set("productCost")} testid="d02-cost" />
          <Field label="Packing" prefix="₹" value={v.packingCost} onChange={set("packingCost")} testid="d02-pack" />
          <Field label="Other variable" prefix="₹" value={v.otherVariable} onChange={set("otherVariable")} testid="d02-other" />
        </div>
        <SliderField label="Target contribution after ads" value={v.targetContributionPct} onChange={set("targetContributionPct")} min={0} max={40} testid="d02-target" />
        <Note>ACOS = ad spend ÷ ad-attributed sales. TACOS divides by total sales — a healthier view of organic halo, but it can hide unprofitable ads.</Note>
      </div>
      <div className="border border-zinc-200 bg-white p-6 h-fit">
        <div className="overline">Ad-spend headroom</div>
        <div className="mt-3">
          <ResultRow label="Fees per unit" value={inr(r.fees)} />
          <ResultRow label="Contribution before ads" value={`${inr(r.contributionBeforeAds)} (${r.contributionPct}%)`} tone={r.negative ? "bad" : undefined} />
          <ResultRow strong label="Break-even ACOS" value={r.negative ? "None — fix the P&L first" : pct(r.breakEvenAcosPct / 100)} />
          <ResultRow strong label="Target ACOS (for 10%+ contribution)" value={r.negative ? "—" : pct(r.targetAcosPct / 100)} tone="good" />
        </div>
        {r.negative && <div className="mt-4"><Note tone="warn">This SKU loses money before advertising. No ACOS target can fix pricing, fees or product cost.</Note></div>}
      </div>
    </div>
  );
}

export function ToolD03() {
  const [v, set] = useNumbers({ contributionPct: 30, returnRatePct: 10, targetContributionPct: 10 });
  const r = useMemo(() => breakEvenRoas(v), [v]);
  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="space-y-5">
        <SliderField label="Contribution margin before ads" hint="Net revenue minus all variable costs, as % of net revenue." value={v.contributionPct} onChange={set("contributionPct")} min={1} max={80} testid="d03-cm" />
        <SliderField label="Expected return / replacement rate" value={v.returnRatePct} onChange={set("returnRatePct")} min={0} max={50} testid="d03-returns" />
        <SliderField label="Target contribution after ads" value={v.targetContributionPct} onChange={set("targetContributionPct")} min={0} max={40} testid="d03-target" />
        <Note>ROAS is measured on revenue; profit lives in contribution. High ROAS can still lose money on thin-margin products — low ROAS can be fine for high-repeat cohorts.</Note>
      </div>
      <div className="border border-zinc-200 bg-white p-6 h-fit">
        <div className="overline">Your ROAS floor</div>
        <div className="mt-3">
          <ResultRow label="Effective contribution (after returns)" value={pct(r.effectiveContributionPct / 100)} />
          <ResultRow label="Break-even ROAS (ignoring returns)" value={r.breakEvenRoasNoReturns ? `${r.breakEvenRoasNoReturns}×` : "—"} />
          <ResultRow strong label="Break-even ROAS (returns included)" value={r.breakEvenRoas ? `${r.breakEvenRoas}×` : "Not achievable"} tone={r.impossible ? "bad" : undefined} />
          <ResultRow strong label={`ROAS for ${v.targetContributionPct}% contribution`} value={r.targetRoas ? `${r.targetRoas}×` : "—"} tone="good" />
        </div>
        {r.impossible && <div className="mt-4"><Note tone="warn">Returns plus your target contribution exceed the margin — that target is not achievable at any ROAS under these economics. Lower the target or improve the margin.</Note></div>}
      </div>
    </div>
  );
}

export function ToolD05() {
  const [v, set] = useNumbers({ cac: 800, firstContribution: 200, repeatContribution: 300, retentionPct: 50, periods: 24 });
  const r = useMemo(() => cacLtv(v), [v]);
  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="space-y-5">
        <Field label="Customer acquisition cost (CAC)" prefix="₹" value={v.cac} onChange={set("cac")} testid="d05-cac" />
        <Field label="First-order contribution" prefix="₹" value={v.firstContribution} onChange={set("firstContribution")} testid="d05-first" />
        <Field label="Contribution per repeat order" prefix="₹" value={v.repeatContribution} onChange={set("repeatContribution")} testid="d05-repeat" />
        <SliderField label="Period-to-period retention" value={v.retentionPct} onChange={set("retentionPct")} min={0} max={95} testid="d05-retention" />
        <SliderField label="Horizon (months)" value={v.periods} onChange={set("periods")} min={3} max={60} suffix="" testid="d05-horizon" />
        <Note>Contribution-based, discounted LTV — not lifetime revenue. If retention is estimated from little history, treat results as hypotheses.</Note>
      </div>
      <div className="border border-zinc-200 bg-white p-6 h-fit">
        <div className="overline">Cohort economics</div>
        <div className="mt-3">
          <ResultRow label="Modelled LTV (contribution)" value={inr(r.ltv)} />
          <ResultRow strong label="LTV : CAC" value={r.ltvCac === null ? "—" : `${r.ltvCac}×`} tone={r.ltvCac !== null && r.ltvCac < 3 ? "bad" : "good"} />
          <ResultRow label="Indicative payback" value={r.paybackMonths ? `~${r.paybackMonths} months` : "Not recovered within horizon"} tone={r.paybackMonths ? undefined : "bad"} />
          <ResultRow label="Max CAC for a 3× ratio" value={inr(r.maxCacForRatio3)} tone="good" />
        </div>
      </div>
    </div>
  );
}
